// The photo editors' action buttons (Studio Editor, AI photo editor, AI Templates): a compact icon-only strip below `xl` — a row under the photo on
// a phone, a column beside it from `sm` — and a labelled column from `xl`. In the icon-only range the label is visually hidden and
// shown as a tooltip instead. Each button shows a spinner while its action runs.
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAdjustmentsHorizontal, IconDimensions, IconInfoCircle, IconLoader2, IconPrinter } from '@tabler/icons-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Shows a spinner while its action runs: until a promise-returning `onClick` settles, or — for an instant one that opens a modal —
// for the frame before that modal paints. A second click while busy is ignored.
const nextPaint = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
export function useBusyAction(onClick, loading = false) {
    const [pending, setPending] = useState(false);
    const busy = loading || pending;
    const run = async () => {
        if (busy || !onClick) return;
        setPending(true);
        try {
            await nextPaint(); // let the spinner show before any heavy work starts
            await onClick();
        } finally {
            setPending(false);
        }
    };
    return [busy, run];
}

export const LABEL_CLS = '!text-sm max-xl:sr-only'; // !text-sm: the global span rule would hold the label at 12px
export const SIDE_BTN = 'max-xl:h-10 max-xl:w-10 max-xl:justify-center max-xl:rounded-xl max-xl:px-0 xl:w-full xl:justify-start';
export function withTip(btn, tip, side = 'left') {
    if (!tip) return btn;
    return (
        <Tooltip>
            <TooltipTrigger asChild>{btn}</TooltipTrigger>
            <TooltipContent side={side} className="z-[2000]">{tip}</TooltipContent>
        </Tooltip>
    );
}

// A compact action button (icon + label, 44px tall — the print button's height), with the busy state above. `compact` = the icon-only
// column, where the tooltip shows the label (else the optional longer `tooltip`).
export function ActionBtn({ icon, label, tooltip, onClick, loading = false, disabled = false, compact = false, tipSide }) {
    const [busy, run] = useBusyAction(onClick, loading);
    return withTip(
        <button
            type="button"
            onClick={run}
            disabled={disabled}
            aria-busy={busy || undefined}
            className={cn('inline-flex h-11 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3.5 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50 aria-busy:cursor-progress aria-busy:opacity-80 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200', SIDE_BTN)}
        >
            {busy ? <IconLoader2 size={18} className="animate-spin" /> : icon}
            <span className={LABEL_CLS}>{label}</span>
        </button>,
        compact ? label : tooltip,
        tipSide,
    );
}
export const btnIcon = (src) => <img src={src} alt="" width={20} height={20} className="shrink-0 dark:invert dark:brightness-110" />;
// Custom size — a measured box, so it doesn't read like the edit button's sliders
export const customSizeIcon = <IconDimensions size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />;

// The green print button — full width in the columns, a square in the icon-only one.
export function PrintButton({ label, onClick, compact = false, tipSide }) {
    const [busy, run] = useBusyAction(onClick);
    return withTip(
        <button
            type="button"
            onClick={run}
            aria-busy={busy || undefined}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-green-500 px-5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-green-600 aria-busy:cursor-progress aria-busy:opacity-80 max-xl:h-10 max-xl:w-10 max-xl:rounded-xl max-xl:px-0 xl:w-full"
        >
            {busy ? <IconLoader2 size={18} className="shrink-0 animate-spin" /> : <IconPrinter size={18} className="shrink-0" />}
            <span className={LABEL_CLS}>{label}</span>
        </button>,
        compact ? label : null,
        tipSide,
    );
}

// The action-row button that opens / closes the edit drawer — the action buttons' look, tinted while open, with an orange dot
// when edits are applied and the drawer is closed.
export function EditToggle({ open, edited, onClick, compact = false, tipSide }) {
    const { t } = useTranslation();
    return withTip(
        <button
            type="button"
            onClick={onClick}
            aria-expanded={open}
            className={cn(
                'relative inline-flex h-11 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium shadow-sm transition-colors',
                open
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-zinc-200 bg-white text-zinc-700 hover:border-primary/50 hover:bg-primary/5 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200',
                SIDE_BTN,
            )}
        >
            <IconAdjustmentsHorizontal size={18} className="shrink-0 text-primary" />
            <span className={LABEL_CLS}>{t('imageGeneration.imageAdjustments')}</span>
            {edited && !open && <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-white dark:ring-zinc-900" />}
        </button>,
        // the label in the icon-only column; elsewhere the reminder that the edits only reach the download / print / copy
        compact ? t('imageGeneration.imageAdjustments') : t('studioEditor.adjustNote'),
        tipSide,
    );
}

const InfoTrigger = forwardRef(function InfoTrigger({ label, ...props }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            {...props}
            className={cn('inline-flex h-11 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3.5 text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/5 data-[state=open]:border-primary data-[state=open]:bg-primary/10 data-[state=open]:text-primary dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200', SIDE_BTN)}
        >
            <IconInfoCircle size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />
            <span className={LABEL_CLS}>{label}</span>
        </button>
    );
});

// The phone's docked action bar: an icon over a small label, no tooltip (a touch screen has no hover). `BarButton` is the look (it can be
// a popover's trigger); `BarAction` adds the busy state above.
export const BarButton = forwardRef(function BarButton({ icon, label, busy = false, tone, dot = false, ...props }, ref) {
    return (
        <button
            ref={ref}
            type="button"
            aria-busy={busy || undefined}
            {...props}
            className={cn(
                'relative flex min-w-[54px] flex-1 shrink-0 flex-col items-center justify-center gap-1 px-1 py-2 transition-colors active:bg-zinc-100 disabled:opacity-50 aria-busy:opacity-80 data-[state=open]:text-primary dark:active:bg-zinc-800',
                tone === 'print' ? 'text-green-600 dark:text-green-400' : 'text-zinc-600 dark:text-zinc-300',
            )}
        >
            <span className="relative flex h-6 items-center justify-center">
                {busy ? <IconLoader2 size={20} className="animate-spin" /> : icon}
                {dot && <span className="absolute -right-1.5 -top-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-white dark:ring-zinc-900" />}
            </span>
            <span className="whitespace-nowrap !text-[10.5px] font-medium leading-none">{label}</span>
        </button>
    );
});
export function BarAction({ onClick, loading = false, ...props }) {
    const [busy, run] = useBusyAction(onClick, loading);
    return <BarButton {...props} busy={busy} onClick={run} />;
}

// "Image info": the photo's details in a popover beside the button (`side`). `onOpenChange` lets the page fetch details on open;
// `trigger` replaces the usual button (the phone's action bar).
export function InfoButton({ children, compact = false, tipSide, side = 'left', onOpenChange, trigger }) {
    const { t } = useTranslation();
    const label = t('imageGeneration.imageInfo');
    return (
        <Popover onOpenChange={onOpenChange}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                        {trigger || <InfoTrigger label={label} aria-label={label} />}
                    </PopoverTrigger>
                </TooltipTrigger>
                {compact && <TooltipContent side={tipSide} className="z-[2000]">{label}</TooltipContent>}
            </Tooltip>
            {/* above the phone's সেটিং sheet (z 99999) too */}
            <PopoverContent side={side} align="start" className="z-[100000] w-80 p-3">
                <p className="mb-2 text-sm !font-semibold text-zinc-800 dark:text-zinc-100">{label}</p>
                {children}
            </PopoverContent>
        </Popover>
    );
}
